import React, { useMemo, useEffect, useState } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Apps as AppsIcon,
  EventBusy as ExpiredIcon,
  ToggleOff as InactiveIcon,
  Security as SecurityIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
  InfoOutlined as InfoOutlinedIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
} from 'recharts';
import { alpha } from '@mui/material/styles';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import PageLoader from '../components/PageLoader';

const STATUS_COLORS = {
  active: '#22c55e',
  pending: '#f59e0b',
  cancelled: '#ef4444',
  expired: '#64748b',
  inactive: '#94a3b8',
};

const STATUS_MEANINGS = {
  active: 'Permit is valid and can be used normally.',
  pending: 'Permit is submitted and waiting for review/approval.',
  cancelled: 'Permit was rejected and is no longer valid.',
  expired: 'Permit validity period ended and renewal is required.',
  inactive: 'Permit is temporarily suspended and not currently usable.',
};

const STATUS_MEANINGS_UR = {
  active: 'اجازت نامہ درست ہے اور عام طور پر استعمال کیا جا سکتا ہے۔',
  pending: 'اجازت نامہ جمع ہو گیا ہے اور جائزے کا انتظار ہے۔',
  cancelled: 'اجازت نامہ منسوخ کر دیا گیا ہے اور اب درست نہیں۔',
  expired: 'اجازت نامے کی میعاد ختم ہو گئی ہے اور تجدید درکار ہے۔',
  inactive: 'اجازت نامہ عارضی طور پر معطل ہے۔',
};

const toSafeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const ACTIVITY_ACTION_COLORS = {
  created: '#2563eb',
  updated: '#6366f1',
  assigned: '#8b5cf6',
  forwarded: '#0ea5e9',
  activated: '#22c55e',
  deactivated: '#94a3b8',
  cancelled: '#ef4444',
  renewed: '#14b8a6',
  rejected: '#dc2626',
  paid: '#22c55e',
  fee_updated: '#f59e0b',
  disputed: '#f59e0b',
  resolved: '#22c55e',
};

const formatTimeAgo = (timestamp) => {
  const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const buildStatusData = (stats) => {
  const entries = [
    { key: 'active', label: 'Active', value: toSafeNumber(stats.activePermits) },
    { key: 'pending', label: 'Pending', value: toSafeNumber(stats.pendingPermits) },
    { key: 'cancelled', label: 'Rejected', value: toSafeNumber(stats.cancelledPermits) },
    { key: 'expired', label: 'Expired', value: toSafeNumber(stats.expiredPermits) },
    { key: 'inactive', label: 'Suspended', value: toSafeNumber(stats.inactivePermits) },
  ];

  return entries.map((entry) => ({
    ...entry,
    color: STATUS_COLORS[entry.key],
  }));
};

const buildRecentTrend = (permits) => {
  if (!Array.isArray(permits) || permits.length === 0) {
    return [];
  }

  const grouped = permits.reduce((accumulator, permit) => {
    if (!permit?.issued_date) return accumulator;
    const date = new Date(permit.issued_date);
    if (Number.isNaN(date.getTime())) return accumulator;

    const label = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    accumulator[label] = (accumulator[label] || 0) + 1;
    return accumulator;
  }, {});

  return Object.entries(grouped).map(([date, count]) => ({ date, count }));
};

const healthLabel = (score) => {
  if (score >= 85) return { label: 'Excellent', color: '#22c55e' };
  if (score >= 70) return { label: 'Good', color: '#10b981' };
  if (score >= 50) return { label: 'Fair', color: '#f59e0b' };
  return { label: 'Needs Attention', color: '#ef4444' };
};

const StatCard = ({ title, value, subtitle, icon: Icon, color, delay = 0 }) => (
  <Card
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 2,
      border: `1px solid ${color}30`,
      background: (theme) => `linear-gradient(145deg, ${alpha(color, 0.12)}, ${theme.palette.background.paper})`,
      transition: 'transform 200ms ease, box-shadow 200ms ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 8px 20px ${color}25`,
      },
    }}
  >
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color, mt: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: `${color}22`,
            color,
            width: 40,
            height: 40,
            border: `1px solid ${color}40`,
          }}
        >
          <Icon fontSize="small" />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [stats, setStats] = useState({
    totalPermits: 0,
    activePermits: 0,
    inactivePermits: 0,
    cancelledPermits: 0,
    expiredPermits: 0,
    pendingPermits: 0,
  });
  const [recentPermits, setRecentPermits] = useState([]);
  const [userFeatures, setUserFeatures] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityError, setActivityError] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let isFetching = false;

    const loadDashboard = async ({ background = false } = {}) => {
      if (!user?.id) return;
      // Skip if a request is already in flight (e.g. a slow response
      // overlapping the next poll tick), and never show the loading
      // skeleton for background refreshes — only the very first load.
      if (isFetching) return;
      isFetching = true;
      if (!background) setLoading(true);

      try {
        const [statsResult, permitsResult, activityResult] = await Promise.allSettled([
          apiClient.get('/permits/stats/'),
          apiClient.get('/permits/', { params: { ordering: '-issued_date', page_size: 5 } }),
          apiClient.get('/permits/recent_activity/'),
        ]);

        if (cancelled) return;

        // Stats/permits are load-bearing for the rest of the page, so a
        // failure there still surfaces the existing error banner.
        if (statsResult.status === 'rejected' || permitsResult.status === 'rejected') {
          throw statsResult.reason || permitsResult.reason;
        }

        setStats(statsResult.value.data || {});
        setRecentPermits(permitsResult.value.data?.results || permitsResult.value.data?.slice?.(0, 5) || []);
        setUserFeatures(Array.isArray(user.features) ? user.features : []);
        setError(null);

        // Recent activity is a supplementary feed — don't let it block or
        // error out the rest of the dashboard if it fails.
        if (activityResult.status === 'fulfilled') {
          setRecentActivity(Array.isArray(activityResult.value.data) ? activityResult.value.data : []);
          setActivityError(false);
        } else {
          console.error(activityResult.reason);
          setActivityError(true);
        }
      } catch (fetchError) {
        if (cancelled) return;
        console.error(fetchError);
        // Don't clobber already-visible data with an error banner on a
        // background refresh — only surface it on the initial load.
        if (!background) setError(t('dashboard.loadError'));
      } finally {
        isFetching = false;
        if (!background) setLoading(false);
      }
    };

    loadDashboard();

    // Keep dashboard numbers fresh without requiring a manual reload —
    // skip ticks while the tab is hidden to avoid pointless background load.
    const intervalId = setInterval(() => {
      if (document.hidden) return;
      loadDashboard({ background: true });
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user, t]);

  const totalPermits = Math.max(toSafeNumber(stats.totalPermits), 1);
  const activePermits = toSafeNumber(stats.activePermits);
  const pendingPermits = toSafeNumber(stats.pendingPermits);
  const expiredPermits = toSafeNumber(stats.expiredPermits);
  const cancelledPermits = toSafeNumber(stats.cancelledPermits);
  const inactivePermits = toSafeNumber(stats.inactivePermits);

  const healthScore = useMemo(() => {
    const productive = activePermits + inactivePermits;
    const blockers = pendingPermits + cancelledPermits + expiredPermits;
    return Math.max(0, Math.min(100, Math.round(((productive - blockers * 0.25) / totalPermits) * 100)));
  }, [activePermits, inactivePermits, pendingPermits, cancelledPermits, expiredPermits, totalPermits]);

  const health = healthLabel(healthScore);
  const statusData = useMemo(() => buildStatusData(stats), [stats]);
  const trendData = useMemo(() => buildRecentTrend(recentPermits), [recentPermits]);

  const barData = useMemo(() => statusData.map((item) => ({ name: item.label, value: item.value })), [statusData]);

  if (loading) {
    return <PageLoader variant="cards" cards={6} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
      <Paper
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 2,
          borderRadius: 3,
          color: '#fff',
          background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 45%, #0ea5e9 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('dashboard.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${toSafeNumber(stats.totalPermits)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
            <Chip label={`Active: ${activePermits}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
            <Chip label={`Health: ${healthScore}%`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }} />
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2, borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(event, value) => setCurrentTab(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab label={t('dashboard.overview')} />
            <Tab label={t('dashboard.rolesAndPermissions')} />
            <Tab label={t('dashboard.recentActivity')} />
          </Tabs>
        </Box>

        {currentTab === 0 && (
          <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={t('dashboard.totalPermits')}
                  value={toSafeNumber(stats.totalPermits)}
                  subtitle={t('dashboard.allPermits')}
                  icon={InventoryIcon}
                  color="#2563eb"
                  delay={0}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={t('common.active')}
                  value={activePermits}
                  subtitle={`${Math.round((activePermits / totalPermits) * 100)}% ${t('dashboard.allPermits')}`}
                  icon={CheckCircleIcon}
                  color="#22c55e"
                  delay={80}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={t('common.pending')}
                  value={pendingPermits}
                  subtitle={`${Math.round((pendingPermits / totalPermits) * 100)}%`}
                  icon={PendingIcon}
                  color="#f59e0b"
                  delay={160}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={t('common.expired')}
                  value={expiredPermits}
                  subtitle={`${Math.round((expiredPermits / totalPermits) * 100)}%`}
                  icon={ExpiredIcon}
                  color="#ef4444"
                  delay={240}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={t('common.cancelled')}
                  value={cancelledPermits}
                  subtitle={`${Math.round((cancelledPermits / totalPermits) * 100)}%`}
                  icon={CancelIcon}
                  color="#dc2626"
                  delay={320}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2}>
                <StatCard
                  title={t('common.inactive')}
                  value={inactivePermits}
                  subtitle={`${Math.round((inactivePermits / totalPermits) * 100)}%`}
                  icon={InactiveIcon}
                  color="#6b7280"
                  delay={400}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} lg={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PieChartIcon sx={{ color: '#1d4ed8', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('dashboard.statusDistribution')}</Typography>
                    </Box>
                    <Box sx={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusData}
                            dataKey="value"
                            nameKey="label"
                            cx="50%"
                            cy="45%"
                            innerRadius="30%"
                            outerRadius="55%"
                            paddingAngle={3}
                          >
                            {statusData.map((entry) => (
                              <Cell key={entry.key} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend iconSize={10} wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BarChartIcon sx={{ color: '#0ea5e9', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('dashboard.statusComparison')}</Typography>
                    </Box>
                    <Box sx={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                            {statusData.map((entry) => (
                              <Cell key={entry.key} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} lg={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TimelineIcon sx={{ color: '#14b8a6', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('dashboard.recentTrend')}</Typography>
                    </Box>
                    <Box sx={{ height: 200 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trendData.length > 0 ? trendData : [{ date: 'N/A', count: 0 }]}>
                          <defs>
                            <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.45} />
                              <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Area type="monotone" dataKey="count" stroke="#0f766e" fill="url(#trendGradient)" strokeWidth={3} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InsightsIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('dashboard.healthInsights')}</Typography>
                      </Box>
                      <MuiTooltip
                        arrow
                        placement="left"
                        title={(
                          <Box sx={{ p: 0.5, maxWidth: 340 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.8 }}>
                              {t('dashboard.healthFormula')}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.8 }}>
                              {t('dashboard.productive')}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.8 }}>
                              {t('dashboard.blockers')}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.8 }}>
                              {t('dashboard.scoreFormula')}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {t('dashboard.healthLabels')}
                            </Typography>
                          </Box>
                        )}
                      >
                        <IconButton size="small" sx={{ color: '#64748b' }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </MuiTooltip>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${health.color}50`,
                        background: `${health.color}12`,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 700 }}>{t('dashboard.systemHealth')}</Typography>
                      <Typography variant="h5" sx={{ color: health.color, fontWeight: 800 }}>
                        {healthScore}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {health.label}
                      </Typography>
                    </Box>

                    <LinearProgress
                      variant="determinate"
                      value={healthScore}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        mb: 1.5,
                        '& .MuiLinearProgress-bar': { backgroundColor: health.color },
                      }}
                    />

                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography variant="caption">• {t('dashboard.healthPositive')}</Typography>
                      <Typography variant="caption">• {t('dashboard.healthNegative')}</Typography>
                      <Typography variant="caption">• {t('dashboard.healthTip')}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 1.5 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('dashboard.statusGuide')}
                </Typography>
                <Grid container spacing={1.5}>
                  {statusData.map((status) => (
                    <Grid item xs={12} sm={6} md={4} key={`status-guide-${status.key}`}>
                      <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <Chip
                          size="small"
                          label={status.label}
                          sx={{
                            fontWeight: 700,
                            bgcolor: `${status.color}22`,
                            color: status.color,
                            border: `1px solid ${status.color}55`,
                            mb: 1,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {i18n.language === 'ur'
                            ? (STATUS_MEANINGS_UR[status.key] || STATUS_MEANINGS[status.key])
                            : STATUS_MEANINGS[status.key]}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('dashboard.recentlyAdded')}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>{t('dashboard.permitId')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('dashboard.type')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Owner</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('dashboard.status')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('dashboard.issuedDate')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentPermits.length > 0 ? (
                        recentPermits.map((permit) => (
                          <TableRow key={permit.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{permit.permit_number || '-'}</TableCell>
                            <TableCell>{permit.vehicle_number || '-'}</TableCell>
                            <TableCell>{permit.owner_name || '-'}</TableCell>
                            <TableCell>
                            <Chip
                                size="small"
                                label={permit.status === 'cancelled' ? 'Rejected' : (permit.status || 'unknown').replace(/^./, (char) => char.toUpperCase())}
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: `${STATUS_COLORS[permit.status] || '#64748b'}22`,
                                  color: STATUS_COLORS[permit.status] || '#64748b',
                                  border: `1px solid ${(STATUS_COLORS[permit.status] || '#64748b')}55`,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {permit.issued_date
                                ? new Date(permit.issued_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                            {t('dashboard.noRecentPermits')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </CardContent>
        )}

        {currentTab === 1 && (
          <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: 2 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SecurityIcon color="primary" />
              {t('dashboard.rolesAndPermissions')}
            </Typography>

            {user?.role ? (
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>
                  {t('dashboard.primaryRole')}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.8 }}>
                  {typeof user.role === 'string'
                    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                    : user.role.name?.charAt(0).toUpperCase() + user.role.name?.slice(1) || 'Unknown'}
                </Typography>
              </Paper>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>{t('dashboard.noRoleAssigned')}</Alert>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.2 }}>
              {t('dashboard.assignedFeatures')} ({userFeatures.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userFeatures.length > 0 ? (
                userFeatures.map((feature, index) => {
                  const displayName = typeof feature === 'string'
                    ? feature.replace(/_/g, ' ')
                    : feature.display_name || feature.name || 'Feature';

                  return (
                    <Chip
                      key={index}
                      icon={<AppsIcon />}
                      label={displayName.replace(/\b\w/g, (char) => char.toUpperCase())}
                      variant="outlined"
                      color="primary"
                    />
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">{t('dashboard.noFeaturesFound')}</Typography>
              )}
            </Box>
          </CardContent>
        )}

        {currentTab === 2 && (
          <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: 2 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon color="primary" />
              {t('dashboard.recentActivity')}
            </Typography>

            {activityError ? (
              <Alert severity="warning">{t('dashboard.activityLoadError')}</Alert>
            ) : recentActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('dashboard.noActivityFound')}</Typography>
            ) : (
              <Box>
                {recentActivity.map((activity, index) => {
                  const isChalan = activity.type === 'chalan';
                  const color = ACTIVITY_ACTION_COLORS[activity.action] || (isChalan ? '#0288d1' : '#6366f1');
                  const actionLabel = activity.action
                    ? activity.action.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
                    : '';

                  return (
                    <Box
                      key={`${activity.type}-${activity.reference}-${activity.timestamp}-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        py: 1.5,
                        borderBottom: index < recentActivity.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(color, 0.15), color }}>
                        {isChalan ? <ReceiptIcon fontSize="small" /> : <InventoryIcon fontSize="small" />}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {activity.performed_by || 'Unknown'}
                          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> {actionLabel} </Box>
                          {activity.reference}
                        </Typography>
                        {activity.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {activity.notes}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatTimeAgo(activity.timestamp)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        )}
      </Card>
    </Box>
  );
}

export default Dashboard;
